// Inventory model + PokéGenie CSV parser for Nexus Dex v7.
// CSV is loaded as raw text via Vite's `?raw` import.
// The 47 columns we accept come from PokéGenie's standard export.

import csvText from "@/data/poke_genie_export.csv?raw";

export type CatchMethod = "Wild" | "Research" | "Raid" | "Trade" | "Egg" | "GBL" | "Unknown";
export type SizeClass = "XXL" | "XXS" | "Normal" | null;
export type CaptureCompleteness = "complete" | "partial" | "csv-only";

// v8, Clean Slate Model. Box Scan is the source of truth.
// Legacy PokéGenie CSV is archived separately and used ONLY as a pre-fill
// suggestion when the user opens deep-capture on a Pokémon they currently own.
export type InventorySource =
  | "box-scan"     // appeared in a PoGo box screenshot, the canonical inventory
  | "manual-deep"  // captured via 2-snap deep flow (full 13-attribute record)
  | "manual-quick" // quick manual add
  | "legacy-csv";  // imported from PokéGenie CSV, lives in the legacy archive only

// Status retained for backwards compatibility, but the only values that
// matter in v8 are "confirmed" (in current box) and "legacy-archived" (CSV).
export type ReconciliationStatus =
  | "confirmed"               // present in current box, trusted
  | "legacy-archived"         // PokéGenie CSV record, archived for reference
  | "csv-only-suspect"        // (deprecated v7, unused in v8)
  | "box-only"                // (deprecated v7, unused in v8)
  | "user-confirmed-transferred"; // (deprecated v7)

export interface InventoryRecord {
  id: string;
  species: string;
  form: string;
  dexNumber: number;
  gender: string;
  cp: number;
  hp: number;
  ivAtk: number;
  ivDef: number;
  ivSta: number;
  ivAvg: number;
  levelMin: number;
  levelMax: number;
  quickMove: string;
  chargeMove1: string;
  chargeMove2: string;
  scanDate: string;
  catchDate: string | null;
  catchLocation: string | null;
  catchMethod: CatchMethod | null;
  weight: string;
  height: string;
  sizeClass: SizeClass;
  isLucky: boolean;
  isShadow: boolean;
  isPurified: boolean;
  isTradedIn: boolean;
  originalTrainer: string | null;
  isDynamax: boolean;
  isGigantamax: boolean;
  markedForPvP: boolean;
  rankG: number | null;
  rankPercentG: number | null;
  rankU: number | null;
  rankPercentU: number | null;
  rankL: number | null;
  rankPercentL: number | null;
  statProdG: number | null;
  statProdU: number | null;
  statProdL: number | null;
  dustCostG: number | null;
  dustCostU: number | null;
  dustCostL: number | null;
  dustCurrent: number | null;
  captureCompleteness: CaptureCompleteness;
  // NEW v7 fields
  source: InventorySource;
  lastSeenInBox: string | null; // ISO date or null
  lastSeenInCsv: string | null; // ISO date or null (Original Scan Date from CSV)
  reconciliationStatus: ReconciliationStatus;
  isShiny: boolean;
  isHundo: boolean; // 15/15/15
  createdAt: number;
  updatedAt: number;
}

// Lightweight CSV parser that handles quoted fields with embedded commas.
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function num(v: string | undefined): number {
  if (!v) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function maybeNum(v: string | undefined): number | null {
  if (!v || v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function maybeStr(v: string | undefined): string | null {
  if (!v || v.trim() === "") return null;
  return v.trim();
}

// Decide a size class from PokéGenie weight/height fields.
// Heuristic: if weight is given and it ends with "kg", we mark XXL/XXS only when
// extremes are specified. PokéGenie itself doesn't tag XXL/XXS in the CSV;
// we leave size class null unless explicit. Extending later is trivial.
function deriveSizeClass(_w: string, _h: string): SizeClass {
  return null;
}

export function parseInventoryCsv(text: string): InventoryRecord[] {
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]).map((h) => h.trim());
  const idxOf = (h: string) => headers.indexOf(h);

  // Pre-compute column indexes for performance
  const I = {
    Index: idxOf("Index"),
    Name: idxOf("Name"),
    Form: idxOf("Form"),
    PokemonNumber: idxOf("Pokemon Number"),
    Gender: idxOf("Gender"),
    CP: idxOf("CP"),
    HP: idxOf("HP"),
    AtkIV: idxOf("Atk IV"),
    DefIV: idxOf("Def IV"),
    StaIV: idxOf("Sta IV"),
    IVAvg: idxOf("IV Avg"),
    LevelMin: idxOf("Level Min"),
    LevelMax: idxOf("Level Max"),
    QuickMove: idxOf("Quick Move"),
    ChargeMove: idxOf("Charge Move"),
    ChargeMove2: idxOf("Charge Move 2"),
    ScanDate: idxOf("Scan Date"),
    CatchDate: idxOf("Catch Date"),
    Weight: idxOf("Weight"),
    Height: idxOf("Height"),
    Lucky: idxOf("Lucky"),
    ShadowPurified: idxOf("Shadow/Purified"),
    Favorite: idxOf("Favorite"),
    Dust: idxOf("Dust"),
    RankPctG: idxOf("Rank % (G)"),
    RankNumG: idxOf("Rank # (G)"),
    StatProdG: idxOf("Stat Prod (G)"),
    DustG: idxOf("Dust Cost (G)"),
    RankPctU: idxOf("Rank % (U)"),
    RankNumU: idxOf("Rank # (U)"),
    StatProdU: idxOf("Stat Prod (U)"),
    DustU: idxOf("Dust Cost (U)"),
    RankPctL: idxOf("Rank % (L)"),
    RankNumL: idxOf("Rank # (L)"),
    StatProdL: idxOf("Stat Prod (L)"),
    DustL: idxOf("Dust Cost (L)"),
    MarkedPvP: idxOf("Marked for PvP use"),
  };

  const out: InventoryRecord[] = [];
  for (let li = 1; li < lines.length; li++) {
    const cells = parseCsvLine(lines[li]);
    if (cells.length < 5) continue;
    const sp = cells[I.Name]?.trim();
    if (!sp) continue;
    const lucky = num(cells[I.Lucky]) === 1;
    const shadowPurified = num(cells[I.ShadowPurified]);
    // Per PokéGenie: 1 = shadow, 2 = purified, 0 = normal
    const isShadow = shadowPurified === 1;
    const isPurified = shadowPurified === 2;
    const idx = num(cells[I.Index]) || li;
    out.push({
      id: `csv-${idx}`,
      species: sp,
      form: cells[I.Form]?.trim() || "",
      dexNumber: num(cells[I.PokemonNumber]),
      gender: cells[I.Gender]?.trim() || "",
      cp: num(cells[I.CP]),
      hp: num(cells[I.HP]),
      ivAtk: num(cells[I.AtkIV]),
      ivDef: num(cells[I.DefIV]),
      ivSta: num(cells[I.StaIV]),
      ivAvg: num(cells[I.IVAvg]),
      levelMin: num(cells[I.LevelMin]),
      levelMax: num(cells[I.LevelMax]),
      quickMove: cells[I.QuickMove]?.trim() || "",
      chargeMove1: cells[I.ChargeMove]?.trim() || "",
      chargeMove2: cells[I.ChargeMove2]?.trim() || "",
      scanDate: cells[I.ScanDate]?.replace(/"/g, "") || "",
      catchDate: maybeStr(cells[I.CatchDate]),
      catchLocation: null, // not in CSV; will be enriched via 2-snap
      catchMethod: null,   // not in CSV
      weight: cells[I.Weight]?.trim() || "",
      height: cells[I.Height]?.trim() || "",
      sizeClass: deriveSizeClass(cells[I.Weight] || "", cells[I.Height] || ""),
      isLucky: lucky,
      isShadow,
      isPurified,
      isTradedIn: false,
      originalTrainer: null,
      isDynamax: false,
      isGigantamax: false,
      markedForPvP: num(cells[I.MarkedPvP]) === 1,
      rankG: maybeNum(cells[I.RankNumG]),
      rankPercentG: maybeNum(cells[I.RankPctG]),
      rankU: maybeNum(cells[I.RankNumU]),
      rankPercentU: maybeNum(cells[I.RankPctU]),
      rankL: maybeNum(cells[I.RankNumL]),
      rankPercentL: maybeNum(cells[I.RankPctL]),
      statProdG: maybeNum(cells[I.StatProdG]),
      statProdU: maybeNum(cells[I.StatProdU]),
      statProdL: maybeNum(cells[I.StatProdL]),
      dustCostG: maybeNum(cells[I.DustG]),
      dustCostU: maybeNum(cells[I.DustU]),
      dustCostL: maybeNum(cells[I.DustL]),
      dustCurrent: maybeNum(cells[I.Dust]),
      captureCompleteness: "csv-only",
      source: "legacy-csv",
      lastSeenInBox: null,
      lastSeenInCsv: cells[I.ScanDate]?.replace(/"/g, "") || null,
      reconciliationStatus: "legacy-archived",
      isShiny: false,
      isHundo: num(cells[I.AtkIV]) === 15 && num(cells[I.DefIV]) === 15 && num(cells[I.StaIV]) === 15,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }
  return out;
}

let _cached: InventoryRecord[] | null = null;
export function loadSeedInventory(): InventoryRecord[] {
  if (_cached) return _cached;
  _cached = parseInventoryCsv(csvText);
  return _cached;
}

// ── Box Scan reconciliation ─────────────────────────────────────────────
// Reconciles a list of CSV records against a list of box-scan records.
// Mutates a copy and returns reconciled list. CSV entries that match a box
// entry get reconciliationStatus="confirmed" and merged box data. Unmatched
// CSV entries stay "csv-only-suspect". Box entries with no CSV match are
// added as "box-only".
export interface BoxScanEntry {
  species: string;
  cp: number;
  isLucky?: boolean;
  isShadow?: boolean;
  isPurified?: boolean;
  isShiny?: boolean;
  isHundo?: boolean;
  dexNumber?: number;
  scanDate: string; // ISO
}

export function makeBoxRecord(b: BoxScanEntry, idSuffix: string | number): InventoryRecord {
  return {
    id: `box-${idSuffix}`,
    species: b.species,
    form: "",
    dexNumber: b.dexNumber || 0,
    gender: "",
    cp: b.cp,
    hp: 0,
    ivAtk: b.isHundo ? 15 : 0,
    ivDef: b.isHundo ? 15 : 0,
    ivSta: b.isHundo ? 15 : 0,
    ivAvg: b.isHundo ? 100 : 0,
    levelMin: 0,
    levelMax: 0,
    quickMove: "",
    chargeMove1: "",
    chargeMove2: "",
    scanDate: b.scanDate,
    catchDate: null,
    catchLocation: null,
    catchMethod: null,
    weight: "",
    height: "",
    sizeClass: null,
    isLucky: !!b.isLucky,
    isShadow: !!b.isShadow,
    isPurified: !!b.isPurified,
    isTradedIn: false,
    originalTrainer: null,
    isDynamax: false,
    isGigantamax: false,
    markedForPvP: false,
    rankG: null, rankPercentG: null,
    rankU: null, rankPercentU: null,
    rankL: null, rankPercentL: null,
    statProdG: null, statProdU: null, statProdL: null,
    dustCostG: null, dustCostU: null, dustCostL: null,
    dustCurrent: null,
    captureCompleteness: "partial",
    source: "box-scan",
    lastSeenInBox: b.scanDate,
    lastSeenInCsv: null,
    reconciliationStatus: "box-only",
    isShiny: !!b.isShiny,
    isHundo: !!b.isHundo,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

// v8 Clean Slate, build the canonical box inventory directly from box-scan
// entries. No reconciliation. Each visible tile becomes one confirmed record.
export function buildBoxInventory(
  boxEntries: BoxScanEntry[],
): InventoryRecord[] {
  return boxEntries.map((b, i) => {
    const rec = makeBoxRecord(b, i);
    rec.reconciliationStatus = "confirmed";
    return rec;
  });
}

// Mark every PokéGenie CSV record as legacy-archived. They live in their own
// list (separate from the box inventory) and never count toward "true tradeable".
export function archiveCsvAsLegacy(csvRecords: InventoryRecord[]): InventoryRecord[] {
  return csvRecords.map((r) => ({
    ...r,
    source: "legacy-csv" as InventorySource,
    reconciliationStatus: "legacy-archived" as ReconciliationStatus,
  }));
}

// Pre-fill match: when the user opens deep-capture on a box-scan record, look
// for a high-confidence match in the legacy CSV archive so we can offer to
// pre-populate IVs, moves, league ranks. "High confidence" = species + cp
// + isLucky + isShadow + isPurified all match exactly. Returns the first match.
export function findPrefillMatch(
  target: { species: string; cp: number; isLucky?: boolean; isShadow?: boolean; isPurified?: boolean },
  legacyCsv: InventoryRecord[],
): InventoryRecord | null {
  const tl = !!target.isLucky;
  const ts = !!target.isShadow;
  const tp = !!target.isPurified;
  for (const r of legacyCsv) {
    if (
      r.species === target.species &&
      r.cp === target.cp &&
      r.isLucky === tl &&
      r.isShadow === ts &&
      r.isPurified === tp
    ) return r;
  }
  return null;
}

// Apply legacy CSV data to a box record as a deep-capture pre-fill. Copies
// IVs, moves, league ranks, catch metadata. The result is a "manual-deep"
// record (the user still needs to confirm via the 2-snap flow).
export function applyPrefill(box: InventoryRecord, legacy: InventoryRecord): InventoryRecord {
  return {
    ...box,
    hp: legacy.hp,
    ivAtk: legacy.ivAtk,
    ivDef: legacy.ivDef,
    ivSta: legacy.ivSta,
    ivAvg: legacy.ivAvg,
    levelMin: legacy.levelMin,
    levelMax: legacy.levelMax,
    quickMove: legacy.quickMove,
    chargeMove1: legacy.chargeMove1,
    chargeMove2: legacy.chargeMove2,
    catchDate: legacy.catchDate,
    catchLocation: legacy.catchLocation,
    catchMethod: legacy.catchMethod,
    weight: legacy.weight,
    height: legacy.height,
    sizeClass: legacy.sizeClass,
    isTradedIn: legacy.isTradedIn,
    originalTrainer: legacy.originalTrainer,
    markedForPvP: legacy.markedForPvP,
    rankG: legacy.rankG, rankPercentG: legacy.rankPercentG,
    rankU: legacy.rankU, rankPercentU: legacy.rankPercentU,
    rankL: legacy.rankL, rankPercentL: legacy.rankPercentL,
    statProdG: legacy.statProdG, statProdU: legacy.statProdU, statProdL: legacy.statProdL,
    dustCostG: legacy.dustCostG, dustCostU: legacy.dustCostU, dustCostL: legacy.dustCostL,
    dustCurrent: legacy.dustCurrent,
    captureCompleteness: "complete",
    source: "manual-deep",
    updatedAt: Date.now(),
  };
}

// Date helper: legacy records older than `days` (using lastSeenInCsv or scanDate).
export function isLegacyOlderThan(rec: InventoryRecord, days: number, now: number = Date.now()): boolean {
  const iso = rec.lastSeenInCsv || rec.scanDate;
  if (!iso) return true;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return true;
  return now - t > days * 24 * 60 * 60 * 1000;
}

// ── League-protect helpers ──────────────────────────────────────────────

export type LeagueProtect =
  | { tier: "great-keeper"; league: "G"; rank: number }
  | { tier: "ultra-keeper"; league: "U"; rank: number }
  | { tier: "great-viable"; league: "G"; rank: number }
  | { tier: "ultra-viable"; league: "U"; rank: number }
  | null;

export function leagueProtect(rec: InventoryRecord): LeagueProtect {
  // Highest priority: Ultra ≥95%, then Great ≥95%, then viable bands.
  if (rec.rankPercentU != null && rec.rankPercentU >= 95) {
    return { tier: "ultra-keeper", league: "U", rank: rec.rankPercentU };
  }
  if (rec.rankPercentG != null && rec.rankPercentG >= 95) {
    return { tier: "great-keeper", league: "G", rank: rec.rankPercentG };
  }
  if (rec.rankPercentG != null && rec.rankPercentG >= 90) {
    return { tier: "great-viable", league: "G", rank: rec.rankPercentG };
  }
  if (rec.rankPercentU != null && rec.rankPercentU >= 90) {
    return { tier: "ultra-viable", league: "U", rank: rec.rankPercentU };
  }
  return null;
}


// Quick stats summary for the inventory header (v8, Clean Slate)
export interface InventoryStats {
  total: number;
  lucky: number;
  shadow: number;
  purified: number;
  markedPvP: number;
  leagueKeepers: number;
  incomplete: number;
  // v8
  confirmed: number;        // every record with status "confirmed"
  deepRecords: number;      // records with full 13-attribute capture (captureCompleteness="complete")
  legacyCount: number;      // size of legacy CSV archive (separate from main inventory)
  totalBoxSize: number;     // headline, what PoGo says you have (e.g. 11,064)
  // Deprecated v7, kept for compatibility but always 0 in v8
  needsReview: number;
}
export function summarize(
  boxRecords: InventoryRecord[],
  legacyCsv: InventoryRecord[] = [],
  totalBoxSize: number = 0,
): InventoryStats {
  let lucky = 0, shadow = 0, purified = 0, markedPvP = 0, leagueKeepers = 0, incomplete = 0;
  let confirmed = 0, deepRecords = 0;
  for (const r of boxRecords) {
    if (r.isLucky) lucky++;
    if (r.isShadow) shadow++;
    if (r.isPurified) purified++;
    if (r.markedForPvP) markedPvP++;
    const lp = leagueProtect(r);
    if (lp && (lp.tier === "great-keeper" || lp.tier === "ultra-keeper")) leagueKeepers++;
    if (r.captureCompleteness !== "complete") incomplete++;
    if (r.reconciliationStatus === "confirmed") confirmed++;
    if (r.captureCompleteness === "complete") deepRecords++;
  }
  return {
    total: boxRecords.length,
    lucky, shadow, purified, markedPvP, leagueKeepers, incomplete,
    confirmed,
    deepRecords,
    legacyCount: legacyCsv.length,
    totalBoxSize: totalBoxSize || confirmed,
    needsReview: 0,
  };
}

// Map a PokéGenie species name to a national dex number for sprite lookup.
// We use the dexNumber from the CSV row directly; `Pokemon Number` is the key.
// For names that have a dex number of 0 (rare CSV gaps), we fallback by name.
const NAME_TO_DEX: Record<string, number> = {
  // Filled lazily; CSV provides the dex number for ~all rows.
};
